import React, { CSSProperties, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Order } from '../../utils/apollo/graphqlSchema';
import { HeaderLabel } from './FormHeadersUI';
import { IconName, TableSortIcon, SvgIcon } from '../../components/Icon';
import useSyndicateTimezoneLabel from '../../utils/hooks/useSyndicateTimezoneLabel';
import { theme } from '../../styles/theme';
import KubitTooltip from '../../components/KubitTooltip';
import isNil from 'lodash/isNil';

interface IProps {
  onClick: () => void;
  activeSortType: Order;
  text: ReactNode;
  iconName?: IconName;
  isActive: boolean;
  shouldAlignRight: boolean;
  showTimeZone?: boolean;
}

const Container = styled.div<{
  $justifyContent: CSSProperties['justifyContent'];
}>`
  display: flex;
  justify-content: ${({ $justifyContent }) => $justifyContent};
`;

const Label = styled(HeaderLabel)<{ $isReversed?: boolean }>`
  display: inline-flex;
  align-items: center;
  column-gap: 4px;
  &:hover {
    cursor: pointer;
    color: ${theme.colors.mainBlue};
  }

  ${({ $isReversed }) =>
    $isReversed &&
    css`
      flex-flow: row-reverse;
    `}
`;
const StyledText = styled.div`
  flex: 0 0 auto;
`;
const StyledSvgIcon = styled(SvgIcon)`
  margin-right: auto;
  margin-left: auto;
`;

const SortableHeaderLabel = ({
  onClick,
  activeSortType,
  text,
  iconName,
  isActive,
  shouldAlignRight,
  showTimeZone = false,
}: IProps) => {
  const shortTimeZone = useSyndicateTimezoneLabel();

  const iconTooltipContent = iconName && typeof text === 'string' ? text : '';
  const sortOrderText = isActive
    ? activeSortType === Order.Asc
      ? 'Ascending'
      : 'Descending'
    : '';

  const tooltipContent = `${
    iconTooltipContent.length > 0
      ? `${iconTooltipContent}${sortOrderText && ' - '}`
      : ''
  }${sortOrderText}`;

  return (
    <Container
      $justifyContent={
        shouldAlignRight ? 'end' : !isNil(iconName) ? 'center' : 'start'
      }
    >
      <KubitTooltip
        content={tooltipContent}
        placement="bottom"
        noOpenDelay={true}
      >
        <Label onClick={onClick} $isReversed={showTimeZone}>
          {showTimeZone && <StyledText>({shortTimeZone})</StyledText>}
          {iconName ? (
            <StyledSvgIcon
              name={iconName}
              width={16}
              height={16}
              isActive={isActive}
              activeColor="currentColor"
              isHoverable={true}
            />
          ) : (
            <StyledText>{text}</StyledText>
          )}
          {isActive && (
            <TableSortIcon isSortAsc={activeSortType === Order.Asc} />
          )}
        </Label>
      </KubitTooltip>
    </Container>
  );
};

export default SortableHeaderLabel;
